import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { useGetGroupQuery, useUpdateAdminGroupMutation } from '../../api/groupApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import EntityFormPage from '../../components/EntityFormPage/EntityFormPage';
import { TextEditor } from '../../components/TextEditor/TextEditor';
import BroadcastToggleField from '../../components/BroadcastToggleField/BroadcastToggleField';
import ChannelsField from '../../components/ChannelsField/ChannelsField';
import TileImageField from '../../components/TileImageField/TileImageField';
import buttons from '../../styles/shared/buttons.module.css';

export default function GroupEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();

  const { data: group, isLoading, isError, error } = useGetGroupQuery(id);
  const [updateGroup, { isLoading: isSaving }] = useUpdateAdminGroupMutation();
  const [tileFile, setTileFile] = useState(null);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { name: '', terms: '', channels: [], geolocationRequired: false, broadcast: true },
  });
  const broadcast = watch('broadcast');
  const name = watch('name');

  useEffect(() => {
    if (group) {
      reset({
        name: group.name,
        terms: group.terms ?? '',
        channels: group.channels ?? [],
        geolocationRequired: group.geolocation_required ?? false,
        broadcast: group.broadcast ?? true,
      });
      setTileFile(null);
    }
  }, [group, reset]);

  useEffect(() => {
    if (isError) showToast('error', 'Groups', errMsg(error, 'Could not load group.'));
  }, [isError, error, showToast]);

  const onSave = async (data) => {
    try {
      await updateGroup({
        id,
        organizationId: group.organization_id,
        name: data.name.trim(),
        tileFile,
        terms: data.terms.trim() || undefined,
        channels: data.channels,
        geolocationRequired: data.geolocationRequired,
        broadcast: data.broadcast,
      }).unwrap();
      showToast('success', 'Groups', 'Group updated.');
      navigate('/admin/groups');
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not save group.'));
    }
  };

  if (isLoading) return <p className="textMuted">Loading…</p>;
  if (!group) return null;

  return (
    <EntityFormPage title="Edit group" backTo="/admin/groups">
      <form onSubmit={handleSubmit(onSave)}>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Name</label>
              <InputText id={field.name} className="w-full mb-3" {...field} />
            </div>
          )}
        />

        <TileImageField
          file={tileFile}
          onFileChange={setTileFile}
          fallbackUrl={group.tile_image_url}
          name={name || group.name}
          onError={(msg) => showToast('error', 'Groups', msg)}
        />

        {!group.is_system && (
          <Controller
            name="terms"
            control={control}
            render={({ field }) => (
              <div className="field">
                <label htmlFor={field.name}>Terms &amp; conditions</label>
                <TextEditor value={field.value} onTextChange={(e) => field.onChange(e.htmlValue ?? '')} />
              </div>
            )}
          />
        )}

        <ChannelsField control={control} broadcast={broadcast} />

        <BroadcastToggleField control={control} setValue={setValue} />

        <Controller
          name="geolocationRequired"
          control={control}
          render={({ field }) => (
            <div className="field flex align-items-center gap-2 mb-3">
              <InputSwitch
                inputId={field.name}
                checked={field.value}
                onChange={(e) => field.onChange(e.value)}
              />
              <label htmlFor={field.name} className="m-0">
                Require geolocation to subscribe
              </label>
            </div>
          )}
        />

        <Button type="submit" label="Save" className={buttons.saveBtn} loading={isSaving} />
      </form>
    </EntityFormPage>
  );
}
